/**
 * ClubWineSelection.js — Wine-club signup wine picker (kind "club-wine-selection").
 *
 * Common Commerce7 club feature — parity with old createWineClubSelection
 * (RedConcierge/static/javascript/main.js): a carousel of the club's wines, each
 * card matching the wine-listing card look (image + "Tasting notes" pill above
 * the title, a centered qty stepper, then a price row — struck-through regular
 * price + bold member price — and a running "Total: $x" line), a
 * "(Minimum N)" counter, and a Review Order button disabled until the minimum
 * bottle count is reached. Minimum is the only enforced rule (no max cap),
 * matching the old widget exactly.
 *
 * Tasting notes opens the same shared wine drawer as the wine-listing card
 * (utils/openWineDrawer.js), keyed off sku (mapped to variant_id/wine_key —
 * the club-signup feed has no separate unique_id). The image click still
 * routes to the chat "tell me more" query (item.message), same split as old
 * main.js (image → cardClicked, Tasting Notes → openTastingNotesDrawer).
 *
 * message.data: { club, items: [{sku, title, imageUrl, price, memberPrice,
 *   quantity, message}], minimumBottles }
 *
 * EVENTS OUT: ws-club-items-changed {items}, ws-club-review-order,
 *   ws-prompt-select {label, value} (image click), ws-wine-drawer-open (Tasting notes)
 */

import { emit } from "../../../core/state.js";
import { attachCarouselNav } from "./carouselNav.js";
import { bindWineDrawerOpen } from "../../../utils/openWineDrawer.js";

const TASTING_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="15" viewBox="0 0 12 15" fill="none"><path d="M3.16667 0.5V3.16667M5.83333 0.5V3.16667M8.5 0.5V3.16667M3.16667 5.83333H7.16667M3.16667 8.5H8.5M3.16667 11.1667H6.5M1.83333 1.83333H9.83333C10.5697 1.83333 11.1667 2.43029 11.1667 3.16667V12.5C11.1667 13.2364 10.5697 13.8333 9.83333 13.8333H1.83333C1.09695 13.8333 0.5 13.2364 0.5 12.5V3.16667C0.5 2.43029 1.09695 1.83333 1.83333 1.83333Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>';

function cleanPrice(v) {
  const n = parseFloat(String(v ?? "").replace(/[^0-9.]/g, ""));
  return Number.isNaN(n) ? 0 : n;
}

export default class ClubWineSelection {
  render(message) {
    const club = message?.data?.club || {};
    const items = Array.isArray(message?.data?.items) ? message.data.items : [];
    const minimumBottles = Number(message?.data?.minimumBottles) || 1;

    const state = items.map((it) => ({
      sku: it.sku,
      // openWineDrawer keys off variant_id/wine_key/unique_id — the club feed
      // only has sku, so use it for both (same identifier Commerce7 cart
      // lines are already keyed by, per cartLineKey in openWineDrawer.js).
      variant_id: it.sku,
      wine_key: it.sku,
      unique_id: it.unique_id,
      title: it.title || "",
      imageUrl: it.imageUrl || it.image || "",
      price: it.price || "",
      memberPrice: it.memberPrice || it.price || "",
      quantity: Number(it.quantity) || 0,
      message: it.message || `Tell me more about ${it.title || "this wine"}`,
    }));

    const wrap = document.createElement("div");
    wrap.className = "ws-club-selection";

    const title = document.createElement("p");
    title.className = "ws-order-title";
    title.textContent = club.title ? `${club.title} — pick your wines` : "Pick your wines";
    wrap.appendChild(title);

    const carousel = document.createElement("div");
    carousel.className = "ws-carousel ws-club-selection-carousel";
    wrap.appendChild(carousel);

    const track = document.createElement("div");
    track.className = "ws-carousel-track ws-club-selection-track";
    carousel.appendChild(track);

    const rowRefs = new Map();

    const footer = document.createElement("div");
    footer.className = "ws-club-selection-footer";
    const counter = document.createElement("div");
    counter.className = "ws-club-selection-counter";
    const reviewBtn = document.createElement("button");
    reviewBtn.type = "button";
    // Same style as the cart panel's "Proceed to checkout"/"View cart".
    reviewBtn.className = "ws-cart-action-btn";
    reviewBtn.textContent = "Continue";
    footer.appendChild(counter);
    footer.appendChild(reviewBtn);
    wrap.appendChild(footer);

    const totalSelected = () => state.reduce((n, s) => n + s.quantity, 0);

    const refresh = () => {
      const total = totalSelected();
      counter.textContent = `Selected wines: ${total} (Minimum ${minimumBottles})`;
      const met = total >= minimumBottles;
      reviewBtn.disabled = !met;
      reviewBtn.style.opacity = met ? "1" : "0.6";
      state.forEach((s) => {
        const refs = rowRefs.get(s.sku);
        if (!refs) return;
        refs.qtyEl.textContent = String(s.quantity);
        refs.totalEl.textContent = `Total: $${(s.quantity * cleanPrice(s.memberPrice)).toFixed(2)}`;
      });
    };

    const openDetail = (s) =>
      emit("ws-prompt-select", { label: s.message, value: s.message });

    state.forEach((s) => {
      const card = document.createElement("div");
      card.className = "ws-card ws-club-selection-card ws-carousel-item";
      card.dataset.sku = s.sku;

      // Head: image only, wrapped the same fixed-height/centered way as the
      // "Shop our wines" wine-listing card (WineCard.js's own *-winecard-head).
      // Tasting notes is its own element below the head, same as WineCard.js.
      const head = document.createElement("div");
      head.className = "ws-club-selection-head";
      if (s.imageUrl) {
        const img = document.createElement("img");
        img.className = "ws-card-img ws-club-selection-img";
        img.src = s.imageUrl;
        img.alt = s.title;
        img.loading = "lazy";
        img.addEventListener("click", () => openDetail(s));
        head.appendChild(img);
      }
      card.appendChild(head);

      const tasting = document.createElement("button");
      tasting.type = "button";
      tasting.className = "ws-club-selection-tasting";
      tasting.innerHTML = `${TASTING_ICON}<span>Tasting notes</span>`;
      bindWineDrawerOpen(tasting, s, card);
      card.appendChild(tasting);

      const name = document.createElement("p");
      name.className = "ws-card-title ws-club-selection-title";
      name.title = s.title;
      name.textContent = s.title;
      card.appendChild(name);

      const stepper = document.createElement("div");
      stepper.className = "ws-club-qty-stepper";
      const minus = document.createElement("button");
      minus.type = "button";
      minus.className = "ws-club-qty-btn ws-club-qty-minus";
      minus.textContent = "−";
      minus.setAttribute("aria-label", "Decrease quantity");
      const qtyEl = document.createElement("span");
      qtyEl.className = "ws-club-qty-value";
      qtyEl.textContent = String(s.quantity);
      const plus = document.createElement("button");
      plus.type = "button";
      plus.className = "ws-club-qty-btn ws-club-qty-plus";
      plus.textContent = "+";
      plus.setAttribute("aria-label", "Increase quantity");
      stepper.appendChild(minus);
      stepper.appendChild(qtyEl);
      stepper.appendChild(plus);
      card.appendChild(stepper);

      const cardFooter = document.createElement("div");
      cardFooter.className = "ws-club-selection-card-footer";

      const priceRow = document.createElement("div");
      priceRow.className = "ws-club-selection-prices";
      if (s.price && s.price !== s.memberPrice) {
        const reg = document.createElement("s");
        reg.className = "ws-order-strike";
        reg.textContent = s.price;
        priceRow.appendChild(reg);
      }
      const mem = document.createElement("span");
      mem.className = "ws-club-selection-member-price";
      mem.textContent = s.memberPrice;
      priceRow.appendChild(mem);
      cardFooter.appendChild(priceRow);

      const totalEl = document.createElement("p");
      totalEl.className = "ws-club-selection-total";
      totalEl.textContent = `Total: $${(s.quantity * cleanPrice(s.memberPrice)).toFixed(2)}`;
      cardFooter.appendChild(totalEl);

      card.appendChild(cardFooter);

      rowRefs.set(s.sku, { qtyEl, totalEl });

      const emitChange = () => {
        emit("ws-club-items-changed", {
          items: state.map(({ sku, quantity }) => ({ sku, quantity })),
        });
      };

      minus.addEventListener("click", () => {
        if (s.quantity <= 0) return;
        s.quantity -= 1;
        refresh();
        emitChange();
      });
      plus.addEventListener("click", () => {
        s.quantity += 1;
        refresh();
        emitChange();
      });

      track.appendChild(card);
    });

    attachCarouselNav(carousel, track);

    reviewBtn.addEventListener("click", () => {
      if (reviewBtn.disabled) return;
      reviewBtn.disabled = true;
      emit("ws-club-review-order");
    });

    refresh();
    return wrap;
  }
}
